import { useId, type FormEvent } from 'react';
import { Button } from '@shared/ui/Button';
import {
  ErrorNotice,
  FieldBox,
  FieldError,
  FormActions,
  FormBox,
  FormGrid,
  FormHint,
  FormTitle,
  Input,
  Select,
} from './styles';

interface AddAccountModalProps {
  bankName: string;
  setBankName: (v: string) => void;
  bik: string;
  setBik: (v: string) => void;
  accountNumber: string;
  setAccountNumber: (v: string) => void;
  corrAccount: string;
  setCorrAccount: (v: string) => void;
  entityName: string;
  setEntityName: (v: string) => void;
  entityInn: string;
  setEntityInn: (v: string) => void;
  entityKpp: string;
  setEntityKpp: (v: string) => void;
  accountType: string;
  setAccountType: (v: string) => void;
  currentBalance: string;
  setCurrentBalance: (v: string) => void;
  /** Остаток не разобран: что исправить в поле. */
  balanceError: string | null;
  purposeLabel: string;
  setPurposeLabel: (v: string) => void;
  /** Сервер не сохранил счёт: причина. */
  error: string | null;
  isSaving: boolean;
  onSubmit: (e: FormEvent) => void;
  onClose: () => void;
}

export function AddAccountModal({
  bankName,
  setBankName,
  bik,
  setBik,
  accountNumber,
  setAccountNumber,
  corrAccount,
  setCorrAccount,
  entityName,
  setEntityName,
  entityInn,
  setEntityInn,
  entityKpp,
  setEntityKpp,
  accountType,
  setAccountType,
  currentBalance,
  setCurrentBalance,
  balanceError,
  purposeLabel,
  setPurposeLabel,
  error,
  isSaving,
  onSubmit,
  onClose,
}: AddAccountModalProps) {
  const id = useId();
  return (
    <FormBox as="form" onSubmit={onSubmit}>
      <FormTitle>Новый счёт</FormTitle>
      <FormHint>
        Счёт ведётся вручную, пока его не покажет подключение к банку: тогда он сам станет
        автоматическим. Загруженная выписка заводит счёт сама, вносить его заранее не обязательно.
      </FormHint>
      <FormHint>
        Корр. счёт, ИНН и КПП владельца попадают в платёжки файла для банка как реквизиты
        плательщика: без них выгрузка в 1С не пройдёт. У ИП КПП нет.
      </FormHint>
      <FormGrid>
        <Input
          placeholder="Банк (напр. ПАО Сбербанк)"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          required
        />
        <Input
          placeholder="БИК (напр. 044525225)"
          value={bik}
          onChange={(e) => setBik(e.target.value)}
        />
        <Input
          placeholder="Номер расчётного счёта (20 цифр)"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          required
        />
        <Input
          placeholder="Корр. счёт банка (20 цифр)"
          value={corrAccount}
          onChange={(e) => setCorrAccount(e.target.value)}
          inputMode="numeric"
        />
        <Input
          placeholder="Владелец счёта (юрлицо, напр. АО «…»)"
          value={entityName}
          onChange={(e) => setEntityName(e.target.value)}
          required
        />
        <Input
          placeholder="ИНН владельца (10 или 12 цифр)"
          value={entityInn}
          onChange={(e) => setEntityInn(e.target.value)}
          inputMode="numeric"
        />
        <Input
          placeholder="КПП владельца (9 цифр)"
          value={entityKpp}
          onChange={(e) => setEntityKpp(e.target.value)}
          inputMode="numeric"
        />
        <Select
          value={accountType}
          onChange={(e) => setAccountType(e.target.value)}
        >
          <option value="settlement">Расчётный счёт</option>
          <option value="special_obs">Спецсчёт (ОБС)</option>
          <option value="deposit">Депозит / Овернайт</option>
          <option value="tax_reserve">Налоговый резерв ЕНС</option>
        </Select>
        <FieldBox>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="Текущий остаток, ₽"
            aria-label="Текущий остаток, ₽"
            value={currentBalance}
            onChange={(e) => setCurrentBalance(e.target.value)}
            aria-invalid={balanceError ? true : undefined}
            aria-describedby={balanceError ? `${id}-balance-error` : undefined}
            required
          />
          {balanceError && <FieldError id={`${id}-balance-error`}>{balanceError}</FieldError>}
        </FieldBox>
        <Input
          placeholder="Назначение счёта"
          value={purposeLabel}
          onChange={(e) => setPurposeLabel(e.target.value)}
        />
      </FormGrid>
      {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
      <FormActions>
        <Button $size="sm" type="submit" disabled={isSaving}>
          {isSaving ? 'Сохранение…' : 'Сохранить счёт'}
        </Button>
        <Button
          $size="sm"
          $variant="ghost"
          type="button"
          onClick={onClose}
        >
          Отмена
        </Button>
      </FormActions>
    </FormBox>
  );
}
